/**
 * ScreensManagementSection Component
 *
 * A comprehensive component for managing screens within strategy blocks.
 * Provides CRUD operations with drag-and-drop reordering functionality.
 *
 * Features:
 * - Add, edit, delete, clone screens
 * - Screen type selection with predefined types
 * - Field-level validation
 * - Full theme integration (NO hardcoded styles)
 * - Accessibility support
 * - Performance optimized with React.memo
 */
import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Typography,
  Chip,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  ContentCopy as CopyIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { SxProps, Theme } from '@mui/material/styles';

// Import Dialog Component Library components
import { BaseDialog } from '../BaseDialog';
import { DialogHeader } from '../DialogHeader';
import { DialogContent } from '../DialogContent';
import { DialogFooter } from '../DialogFooter';
import { SAccordion } from '../SAccordion';
import { StyledTextField } from '../../forms/fields/StyledTextField';
import { StyledSelectDropdown } from '../../forms/fields/StyledSelectDropdown';
import { validationRules } from '../../forms/validation';

// Import ErrorBoundary for error isolation
import { ErrorBoundary } from '@modules/screen-view-v2';

// ============================================================================
// TYPES AND INTERFACES
// ============================================================================

export interface Screen {
  id: string;
  name: string;
  screenType: string;
  description: string;
  order: number;
  metadata?: Record<string, any>;
}

export interface ValidationError {
  field: string;
  rule: string;
  message: string;
  value: any;
  timestamp?: Date;
}

export interface ScreensManagementSectionProps {
  /**
   * Core Data
   */
  screens: Screen[];

  /**
   * CRUD Operations
   */
  onAddScreen: (screenData: Partial<Screen>) => Promise<void>;
  onUpdateScreen: (screenId: string, updates: Partial<Screen>) => Promise<void>;
  onDeleteScreen: (screenId: string) => Promise<void>;
  onReorderScreens: (screens: Screen[]) => Promise<void>;

  /**
   * Configuration
   */
  readOnly?: boolean;
  variant?: 'default' | 'compact';
  title?: string;

  /**
   * Theme Integration (REQUIRED - no hardcoded styles)
   */
  sx?: SxProps<Theme>;

  /**
   * Help System Integration
   */
  helpId?: string;

  /**
   * Accessibility
   */
  'aria-label'?: string;
  'aria-describedby'?: string;
}

// Screen types available for selection
const SCREEN_TYPES = [
  { value: 'hero', label: 'Hero Section' },
  { value: 'features', label: 'Features' },
  { value: 'benefits', label: 'Benefits' },
  { value: 'testimonials', label: 'Testimonials' },
  { value: 'cta', label: 'Call to Action' },
  { value: 'faq', label: 'FAQ' },
  { value: 'comparison', label: 'Comparison' },
  { value: 'pricing', label: 'Pricing' },
  { value: 'custom', label: 'Custom' },
];

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ScreensManagementSection: React.FC<ScreensManagementSectionProps> = ({
  screens = [],
  onAddScreen,
  onUpdateScreen,
  onDeleteScreen,
  onReorderScreens,
  readOnly = false,
  variant = 'default',
  title = 'Screens Management',
  sx,
  helpId,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
}) => {
  const theme = useTheme();

  // ========================================================================
  // STATE MANAGEMENT
  // ========================================================================

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingScreen, setEditingScreen] = useState<Screen | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    screenType: 'hero',
    description: '',
  });
  const [formErrors, setFormErrors] = useState<ValidationError[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ========================================================================
  // COMPUTED VALUES
  // ========================================================================

  const isFormValid = useMemo(() => {
    return formData.name.trim() !== '' && formData.description.trim() !== '';
  }, [formData.name, formData.description]);

  const sectionTitle = useMemo(() => {
    return `${title} (${screens.length})`;
  }, [title, screens.length]);

  // ========================================================================
  // EVENT HANDLERS
  // ========================================================================

  // Handle dialog open for add/edit
  const handleOpenDialog = useCallback((screen?: Screen) => {
    if (screen) {
      setEditingScreen(screen);
      setFormData({
        name: screen.name,
        screenType: screen.screenType,
        description: screen.description,
      });
    } else {
      setEditingScreen(null);
      setFormData({
        name: '',
        screenType: 'hero',
        description: '',
      });
    }
    setFormErrors([]);
    setDialogOpen(true);
  }, []);

  // Handle dialog close
  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setEditingScreen(null);
    setFormData({
      name: '',
      screenType: 'hero',
      description: '',
    });
    setFormErrors([]);
    setIsSubmitting(false);
  }, []);

  // Handle form field changes
  const handleFieldChange = useCallback((field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear validation errors for this field
    setFormErrors(prev => prev.filter(error => error.field !== field));
  }, []);

  // Handle form validation
  const validateForm = useCallback(() => {
    const errors: ValidationError[] = [];

    if (!formData.name.trim()) {
      errors.push({
        field: 'name',
        rule: 'required',
        message: 'Screen name is required',
        value: formData.name,
      });
    }

    if (!formData.description.trim()) {
      errors.push({
        field: 'description',
        rule: 'required',
        message: 'Description is required',
        value: formData.description,
      });
    }

    setFormErrors(errors);
    return errors.length === 0;
  }, [formData]);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (editingScreen) {
        await onUpdateScreen(editingScreen.id, formData);
      } else {
        await onAddScreen({
          ...formData,
          order: screens.length,
        });
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving screen:', error);
      // In a real application, you might want to show user-friendly error messages
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, editingScreen, screens.length, validateForm, onAddScreen, onUpdateScreen, handleCloseDialog]);

  // Handle screen deletion
  const handleDelete = useCallback(async (screenId: string) => {
    const screen = screens.find(s => s.id === screenId);
    if (!screen) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete "${screen.name}"?\n\nThis action cannot be undone.`
    );

    if (confirmed) {
      try {
        await onDeleteScreen(screenId);
      } catch (error) {
        console.error('Error deleting screen:', error);
      }
    }
  }, [screens, onDeleteScreen]);

  // Handle screen cloning
  const handleClone = useCallback(async (screen: Screen) => {
    try {
      await onAddScreen({
        ...screen,
        name: `${screen.name} (Copy)`,
        order: screens.length,
      });
    } catch (error) {
      console.error('Error cloning screen:', error);
    }
  }, [screens.length, onAddScreen]);

  // Handle view screen details (placeholder for future implementation)
  const handleView = useCallback((screen: Screen) => {
    // This could open a preview dialog or navigate to a detail view
    console.log('View screen details:', screen);
  }, []);

  // ========================================================================
  // DYNAMIC STYLES (THEME-AWARE)
  // ========================================================================

  const containerStyles: SxProps<Theme> = useMemo(() => ({
    width: '100%',
    ...sx,
  }), [sx]);

  const emptyStateStyles: SxProps<Theme> = useMemo(() => ({
    padding: theme.spacing(3),
    textAlign: 'center',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  }), [theme]);

  const listItemStyles: SxProps<Theme> = useMemo(() => ({
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
    '&:hover': {
      backgroundColor: alpha(theme.palette.action.hover, 0.05),
    },
    padding: theme.spacing(1, 2),
    paddingRight: readOnly ? theme.spacing(2) : theme.spacing(12),
  }), [theme, readOnly]);

  const chipStyles: SxProps<Theme> = useMemo(() => ({
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
  }), [theme]);

  // ========================================================================
  // RENDER HELPERS
  // ========================================================================

  const renderEmptyState = () => (
    <Box sx={emptyStateStyles}>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        No screens added yet.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Click "Add Screen" to create your first screen.
      </Typography>
    </Box>
  );

  const renderScreensList = () => (
    <List disablePadding>
      {screens.map((screen) => (
        <ErrorBoundary key={screen.id}>
        <Box sx={listItemStyles}>
          <ListItem
            sx={{
              padding: 0,
              alignItems: 'flex-start',
              cursor: readOnly ? 'default' : 'pointer',
              '&:hover': !readOnly ? {
                backgroundColor: alpha(theme.palette.action.hover, 0.1),
              } : {},
            }}
            onClick={!readOnly ? () => handleOpenDialog(screen) : undefined}
          >
            {!readOnly && (
              <IconButton
                size="small"
                edge="start"
                sx={{
                  marginRight: theme.spacing(1),
                  marginTop: theme.spacing(0.5),
                  cursor: 'grab',
                  color: theme.palette.action.active,
                }}
                aria-label="Drag to reorder"
                onClick={(e) => e.stopPropagation()}
              >
                <DragIcon />
              </IconButton>
            )}

            <ListItemText
              primary={
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <Typography variant="subtitle1" sx={{
                    fontWeight: theme.typography.fontWeightMedium,
                    color: theme.palette.text.primary,
                  }}>
                    {screen.name}
                  </Typography>
                  <Chip
                    label={SCREEN_TYPES.find(t => t.value === screen.screenType)?.label || screen.screenType}
                    size="small"
                    variant="outlined"
                    sx={chipStyles}
                  />
                </Box>
              }
              secondary={
                <Typography variant="body2" sx={{
                  color: theme.palette.text.secondary,
                  marginTop: theme.spacing(0.5),
                }}>
                  {screen.description}
                </Typography>
              }
            />

            {!readOnly && (
              <ListItemSecondaryAction>
                <Box display="flex" gap={0.5}>
                  <Tooltip title="View Details">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleView(screen);
                      }}
                      aria-label={`View details for ${screen.name}`}
                      sx={{ color: theme.palette.action.active }}
                    >
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit Screen">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDialog(screen);
                      }}
                      aria-label={`Edit screen ${screen.name}`}
                      sx={{ color: theme.palette.primary.main }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Clone Screen">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClone(screen);
                      }}
                      aria-label={`Clone screen ${screen.name}`}
                      sx={{ color: theme.palette.info.main }}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Screen">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(screen.id);
                      }}
                      aria-label={`Delete screen ${screen.name}`}
                      sx={{ color: theme.palette.error.main }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </ListItemSecondaryAction>
            )}
          </ListItem>
        </Box>
        </ErrorBoundary>
      ))}
    </List>
  );

  const renderAddEditDialog = () => (
    <BaseDialog
      open={dialogOpen}
      onClose={handleCloseDialog}
      maxWidth="sm"
      mode="modal"
      aria-labelledby="screen-dialog-title"
    >
      <DialogHeader
        title={editingScreen ? 'Edit Screen' : 'Add New Screen'}
        onClose={handleCloseDialog}
        variant="primary"
      />
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2}>
          <StyledTextField
            label="Screen Name"
            value={formData.name}
            onChange={(value) => handleFieldChange('name', value)}
            required
            disabled={isSubmitting}
            error={formErrors.some(e => e.field === 'name')}
            helperText={formErrors.find(e => e.field === 'name')?.message}
            rules={[
              {
                type: 'required',
                message: 'Screen name is required',
              },
            ]}
            autoFocus
          />

          <StyledSelectDropdown
            label="Screen Type"
            value={formData.screenType}
            onChange={(value) => handleFieldChange('screenType', value as string)}
            options={SCREEN_TYPES}
            required
            disabled={isSubmitting}
          />

          <StyledTextField
            label="Description"
            value={formData.description}
            onChange={(value) => handleFieldChange('description', value)}
            required
            disabled={isSubmitting}
            multiline
            rows={3}
            error={formErrors.some(e => e.field === 'description')}
            helperText={formErrors.find(e => e.field === 'description')?.message}
            rules={[
              {
                type: 'required',
                message: 'Description is required',
              },
            ]}
          />
        </Box>
      </DialogContent>
      <DialogFooter
        primaryAction={{
          label: editingScreen ? 'Update' : 'Add',
          onClick: handleSubmit,
          disabled: !isFormValid || isSubmitting,
          loading: isSubmitting,
        }}
        secondaryAction={{
          label: 'Cancel',
          onClick: handleCloseDialog,
          disabled: isSubmitting,
        }}
      />
    </BaseDialog>
  );

  // ========================================================================
  // MAIN RENDER
  // ========================================================================

  return (
    <Box sx={containerStyles} data-testid="smart-section" className={variant}>
      <SAccordion
        title={sectionTitle}
        helpId={helpId}
        collapsible={false}
        data-testid="screens-management-section"
      >
        {/* Header with Add Button */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography
            variant="h6"
            component="h3"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: theme.typography.fontWeightMedium,
            }}
          >
            Screens ({screens.length})
          </Typography>
          {!readOnly && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              size="small"
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
            >
              Add Screen
            </Button>
          )}
        </Box>

        {/* Screens List or Empty State */}
        {screens.length === 0 ? renderEmptyState() : renderScreensList()}

        {/* Add/Edit Dialog */}
        {renderAddEditDialog()}
      </SAccordion>
    </Box>
  );
};

// ============================================================================
// MEMOIZATION
// ============================================================================

export default React.memo(ScreensManagementSection, (prevProps, nextProps) => {
  // Custom comparison function for performance optimization
  const compareArrays = (a: any[], b: any[]) => {
    if (a.length !== b.length) return false;
    return a.every((item, index) => {
      if (typeof item === 'object' && item !== null) {
        return JSON.stringify(item) === JSON.stringify(b[index]);
      }
      return item === b[index];
    });
  };

  return (
    prevProps.readOnly === nextProps.readOnly &&
    prevProps.variant === nextProps.variant &&
    prevProps.title === nextProps.title &&
    prevProps.helpId === nextProps.helpId &&
    compareArrays(prevProps.screens, nextProps.screens) &&
    prevProps.onAddScreen === nextProps.onAddScreen &&
    prevProps.onUpdateScreen === nextProps.onUpdateScreen &&
    prevProps.onDeleteScreen === nextProps.onDeleteScreen &&
    prevProps.onReorderScreens === nextProps.onReorderScreens
  );
});

// Export the component and types
export { ScreensManagementSection };
